/* eslint-disable no-console */
import Container from "@mui/material/Container";
import { FormType, FormErrors, Locales } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { v4 } from "uuid";

import { AppFormProvider } from "../../../components/AppProvider/AppFormProvider/AppFormProvider";
import { FrontendDonationFormProvider } from "../../../providers/FrontendDonationFormProvider";

import { DonationFormHead } from "@/components/Head/DonationFormHead/DonationFormHead";
import { ZeffyHead } from "@/components/Head/ZeffyHead";
import NotFoundWithProviders from "@/components/NotFound/NotFoundWithProviders";
import { DonationFormV2Page } from "@/features/FormV2/DonationFormV2Page";
import { addCacheTagHeaderToServerResponse } from "@/helpers/cache";
import { getSSRDonationForm, getTranslation, provideOrganization } from "@/helpers/getStaticProps";
import { useIsEmbeddedModal } from "@/hooks/useEmbeddedModal";
import { useHTMLResizePostMessage } from "@/hooks/useHTMLResizePostMessage";
import { useTheme } from "@/hooks/useTheme";
import { ServerSideContext } from "@/types/app";
import { DonationFormFrontendPageOutput } from "@/types/trpc";

type EmbedDonationFormPageProps =
  | {
      donationForm: DonationFormFrontendPageOutput;
      isDeleted?: false;
    }
  | {
      donationForm?: null;
      isDeleted: true;
    };

const EmbedDonationFormPageV2 = ({ donationForm, isDeleted }: EmbedDonationFormPageProps) => {
  const theme = useTheme({ color: donationForm?.color });
  useHTMLResizePostMessage();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const isEmbeddedModal = useIsEmbeddedModal(query);

  if (isDeleted) {
    return (
      <>
        <ZeffyHead />
        <NotFoundWithProviders deletedForm />
      </>
    );
  }

  if (!donationForm.donationFormFields) {
    return null;
  }
  return (
    <>
      {/* Nothing should wrap or block the Head component */}
      <DonationFormHead donationForm={donationForm} metaContentType="donationForm" />
      <AppFormProvider
        theme={theme}
        organization={donationForm.organization}
        formType={FormType.DonationForm}
        formData={donationForm}
        fields={donationForm.donationFormFields}
        isEmbed
        shouldDisplayLogin={false}
        isFundraiser={false}
        themeColor={donationForm.color}
      >
        <FrontendDonationFormProvider donationForm={donationForm}>
          <Container disableGutters={!isEmbeddedModal}>
            <DonationFormV2Page isEmbed />
          </Container>
        </FrontendDonationFormProvider>
      </AppFormProvider>
    </>
  );
};
export const getServerSideProps = async (context: ServerSideContext<{ donationFormId: string }>) => {
  const requestId = (context.req?.headers?.["x-request-id"] as string) || v4();
  const { donationFormId } = context.params;

  addCacheTagHeaderToServerResponse(context.res, donationFormId);

  const locale = context.locale.toUpperCase() as Locales;
  try {
    const [donationFormResponse, translations] = await Promise.all([
      getSSRDonationForm({ donationFormIdOrUrlPath: donationFormId, requestId }),
      getTranslation(context.locale),
    ]);

    if (donationFormResponse.error) {
      const code = donationFormResponse.error.code;

      if (code === FormErrors.FormDeleted) {
        if (context.res) {
          context.res.statusCode = 410;
        }

        console.log(`Form deleted - redirecting to gone donationFormId=${donationFormId} ssrId=${requestId}`);
        return {
          props: {
            ...translations,
            isDeleted: true,
          },
        };
      }

      console.log(
        `NEXT_ERROR: form not found - redirecting to notFound donationFormId=${donationFormId} ssrId=${requestId}`
      );
      return { props: {}, notFound: true };
    }

    return {
      props: {
        ...translations,
        donationForm: donationFormResponse.data.organization
          ? donationFormResponse.data
          : provideOrganization(donationFormResponse.data, locale),
      },
    };
  } catch (error) {
    console.log(
      `NEXT_ERROR: request failed - redirecting to notFound donationFormId=${donationFormId} ssrId=${requestId}`,
      error
    );
    return { props: {}, notFound: true };
  }
};
export default EmbedDonationFormPageV2;
